import { type ApiResponse, invokeFunction, resolveAccessToken } from '../api'
import { toApiRequestError } from '../errors'
import { config } from '../supabase'

interface SignUploadResponse {
    signedUrl: string
    evidence_id: string
}

interface CommitEvidenceResponse {
    evidence_id: string
    status: string
}

interface SignedDownloadResponse {
    download_url: string
}

const SIGNED_UPLOAD_MAX_ATTEMPTS = 5
const SIGNED_UPLOAD_RETRY_DELAYS_MS = [250, 500, 1_000, 2_000]

type EvidenceInvokeOptions = {
    body?: unknown
    method?: 'POST' | 'GET' | 'PUT' | 'PATCH' | 'DELETE'
    path?: string
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null
}

function asSignUploadResponse(value: unknown): SignUploadResponse | null {
    if (!isRecord(value)) return null
    if (typeof value.signedUrl !== 'string' || !value.signedUrl.trim()) return null
    if (typeof value.evidence_id !== 'string' || !value.evidence_id.trim()) return null
    return {
        signedUrl: value.signedUrl,
        evidence_id: value.evidence_id,
    }
}

function asCommitEvidenceResponse(value: unknown): CommitEvidenceResponse | null {
    if (!isRecord(value)) return null
    if (typeof value.evidence_id !== 'string' || !value.evidence_id.trim()) return null
    if (typeof value.status !== 'string' || !value.status.trim()) return null
    return {
        evidence_id: value.evidence_id,
        status: value.status,
    }
}

function asSignedDownloadResponse(value: unknown): SignedDownloadResponse | null {
    if (!isRecord(value)) return null
    if (typeof value.download_url !== 'string' || !value.download_url.trim()) return null
    return { download_url: value.download_url }
}

async function sha256Hex(file: File): Promise<string> {
    const buffer = await file.arrayBuffer()
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer)
    const bytes = Array.from(new Uint8Array(hashBuffer))
    return bytes.map((byte) => byte.toString(16).padStart(2, '0')).join('')
}

function delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
}

function isTransientUploadError(error: ApiResponse<unknown>['error']): boolean {
    if (!error) return false
    if (typeof error.status === 'number' && (error.status === 429 || error.status >= 500)) return true
    return /network|protocol|timeout|temporar/i.test(error.message)
}

async function invokeEvidenceFunctionWithRetry<T>(options: EvidenceInvokeOptions): Promise<ApiResponse<T>> {
    let lastResult: ApiResponse<T> | null = null

    for (let attempt = 1; attempt <= SIGNED_UPLOAD_MAX_ATTEMPTS; attempt += 1) {
        const result = await invokeFunction<T>('evidence', options)
        if (!result.error) return result
        lastResult = result

        if (!isTransientUploadError(result.error) || attempt === SIGNED_UPLOAD_MAX_ATTEMPTS) {
            return result
        }

        await delay(SIGNED_UPLOAD_RETRY_DELAYS_MS[attempt - 1] ?? 2_000)
    }

    return lastResult ?? { data: null, error: null }
}

async function putSignedUpload(signedUrl: string, file: File): Promise<void> {
    let lastError: unknown = null

    for (let attempt = 1; attempt <= SIGNED_UPLOAD_MAX_ATTEMPTS; attempt += 1) {
        try {
            const uploadResponse = await fetch(signedUrl, {
                method: 'PUT',
                body: file,
                headers: {
                    'Content-Type': file.type,
                },
            })

            if (uploadResponse.ok) return
            lastError = new Error(`Upload failed with HTTP ${uploadResponse.status}`)
        } catch (error) {
            lastError = error
        }

        if (attempt < SIGNED_UPLOAD_MAX_ATTEMPTS) {
            await delay(SIGNED_UPLOAD_RETRY_DELAYS_MS[attempt - 1] ?? 2_000)
        }
    }

    if (lastError instanceof Error && lastError.message) {
        throw new Error(lastError.message)
    }
    throw new Error('Upload failed')
}

/**
 * Evidence Service [FE.4.1]
 * Uses backend `evidence` route paths (sign-upload -> upload -> commit).
 */
export async function uploadEvidence(visitId: string, file: File, options?: { evidenceId?: string }) {
    const signRes = await invokeEvidenceFunctionWithRetry<unknown>({
        method: 'POST',
        path: `/visits/${visitId}/evidence:sign-upload`,
        body: {
            original_filename: file.name,
            content_type: file.type,
            size_bytes: file.size,
            evidence_id: options?.evidenceId,
        },
    })

    if (signRes.error || !signRes.data) {
        throw toApiRequestError(signRes.error, 'Failed to request signed upload URL')
    }

    const signData = asSignUploadResponse(signRes.data)
    if (!signData) {
        throw new Error('Malformed evidence sign-upload response from server')
    }

    await putSignedUpload(signData.signedUrl, file)

    const checksum = await sha256Hex(file)
    const commitRes = await invokeEvidenceFunctionWithRetry<unknown>({
        method: 'POST',
        path: `/visits/${visitId}/evidence:commit`,
        body: {
            evidence_id: signData.evidence_id,
            checksum_sha256: checksum,
            content_type: file.type,
            size_bytes: file.size,
        },
    })

    if (commitRes.error || !commitRes.data) {
        throw toApiRequestError(commitRes.error, 'Evidence commit failed')
    }

    const commitData = asCommitEvidenceResponse(commitRes.data)
    if (!commitData) {
        throw new Error('Malformed evidence commit response from server')
    }

    return { path: commitData.evidence_id }
}

export async function downloadEvidenceBlob(visitId: string, evidenceId: string): Promise<Blob> {
    const signedRes = await invokeFunction<unknown>('evidence', {
        method: 'GET',
        path: `/visits/${visitId}/evidence/${evidenceId}:download`,
    })

    if (signedRes.error || !signedRes.data) {
        throw toApiRequestError(signedRes.error, 'Failed to request evidence download')
    }

    const signedData = asSignedDownloadResponse(signedRes.data)
    if (!signedData) {
        throw new Error('Malformed evidence download response from server')
    }

    const accessToken = await resolveAccessToken()
    if (!accessToken) {
        throw new Error('Your session has expired. Please sign in again.')
    }

    const response = await fetch(signedData.download_url, {
        headers: {
            apikey: config.supabaseAnonKey,
            Authorization: `Bearer ${accessToken}`,
        },
    })
    if (!response.ok) {
        throw new Error('Evidence download failed')
    }
    return response.blob()
}
